/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { map, switchMap, tap } from 'rxjs/operators';
import { Observable, combineLatest, of } from 'rxjs';
import { Ticket, TicketType } from '@touchplan/common';

import { PlanState } from './plan-state.service';
import { CustomFieldType } from '../app-data/state/CustomFieldsApi';
import { CustomFieldService } from '../modules/custom-field/services/custom-field.services';
import { ICustomFieldTicket, ICustomFieldTicketValue } from '../app-data/models/CustomFieldTicketValue';
import { CustomFieldDefinition, ICustomFieldDefinition } from '../app-data/models/CustomFieldDefinition';
import { HoverOverUserPreferencesService, IHoverOverUserPreferences } from './hover-over-preferences.service';

/**
 * HoverOverCustomFieldManager is responsible for handling the logic for the hover over custom fields.
 * everithing it related to the hover over Services and the custom fields services is handled here.
 */

@Injectable({
	providedIn: 'root',
})
export class HoverOverCustomFieldManager {
	public getHoverOverCustomFieldsData$(
		userId: string,
		projectId: string,
		ticket: Ticket
	): Observable<{ label: string; data: string }[]> {
		const ticketType: TicketType = (ticket.view?.type as TicketType) || TicketType.Task;
		const roleId = this.customFieldService.getRoleId(ticket.view);
		if (!ticketType || !roleId) return of(null);
		return this.planState.customFieldsApi.getFilteredDefinitionsForTicket$(ticketType, roleId).pipe(
			switchMap((customFields) => {
				return combineLatest([
					this.planState.customFieldsApi.customFieldTicketValues.data.getChildOb$(ticket.$id),
					this.getHoverEnabledCustomFieldIds$(userId, projectId),
					this.hoverOverUserPreferencesService.hoverOverUserProjectPreferencesChanges$(userId, projectId),
				]).pipe(
					map(([ticketValues, enabledCustomFieldIds, hoverOverSettings]) => {
						if (!ticketValues?.payload?.view) return null;
						return this.filterTicketEnabledCustomFields(
							ticketValues.payload?.view,
							enabledCustomFieldIds,
							hoverOverSettings
						);
					}),
					map((ticketValues) => this.mapCustomFieldValuesToHoverOption(customFields, ticketValues))
				);
			})
		);
	}

	private getCustomFieldValueLabel(
		customFieldDef: ICustomFieldDefinition,
		ticketValue: ICustomFieldTicketValue
	): { label: string; data: string } {
		let customFieldValue = null;
		switch (customFieldDef.type) {
			case CustomFieldType.Text:
			case CustomFieldType.Link:
				customFieldValue = ticketValue.stringValue || ticketValue?.value;
				return {
					label: customFieldDef.label,
					data: customFieldValue ? `${customFieldValue}` : null,
				};
			case CustomFieldType.Number:
				customFieldValue = ticketValue?.numericValue || ticketValue?.value;
				return {
					label: customFieldDef.label,
					data: customFieldValue ? `${customFieldValue} ${customFieldDef.unit || ''}` : null,
				};
			case CustomFieldType.DropDownList:
				return {
					label: customFieldDef.label,
					data:
						customFieldDef.listOfValues.view.data.find(
							(val) => val.id === ticketValue?.idValue || val.id === ticketValue?.value
						)?.label || null,
				};
		}
		console.log('unknown custom field type when displaying label', customFieldDef);
		return { label: '', data: '' };
	}

	private mapCustomFieldValuesToHoverOption(
		customFieldDefinitions: CustomFieldDefinition[],
		ticketCustomFieldValues: ICustomFieldTicket
	): { label: string; data: string }[] {
		if (!customFieldDefinitions.length || !ticketCustomFieldValues) return [];
		return Object.keys(ticketCustomFieldValues)
			.map((customFieldDefKey) => {
				const customFieldDef = customFieldDefinitions.find(
					(customFieldDef) => customFieldDef.$id === customFieldDefKey
				).view;
				return this.getCustomFieldValueLabel(
					customFieldDef,
					ticketCustomFieldValues[customFieldDefKey] as ICustomFieldTicketValue
				);
			})
			.filter((option) => !!option.data);
	}

	private getHoverEnabledCustomFieldIds$(userId: string, projectId: string): Observable<string[]> {
		return this.hoverOverUserPreferencesService.hoverOverUserProjectEnabledCustomFieldsChanges$(
			userId,
			projectId
		);
	}

	private filterTicketEnabledCustomFields(
		customFieldValues: ICustomFieldTicket,
		enabledCustomFieldIds: string[],
		hoverOverSettings: Partial<IHoverOverUserPreferences>
	): ICustomFieldTicket {
		if (hoverOverSettings?.displayAllCustomFields) {
			return customFieldValues;
		}
		const enabledTicketCustomFieldValues: ICustomFieldTicket = {};
		Object.keys(customFieldValues).forEach((key) => {
			if (enabledCustomFieldIds.includes(key)) {
				enabledTicketCustomFieldValues[key] = customFieldValues[key];
			}
		});
		return enabledTicketCustomFieldValues;
	}

	constructor(
		private planState: PlanState,
		private customFieldService: CustomFieldService,
		private hoverOverUserPreferencesService: HoverOverUserPreferencesService
	) {}
}
